import getMergedAttributes from "@secureo/common/reducers/productDetailPageReducer/utils/getMergedAttributes"
import getPricePortions from "@secureo/common/reducers/productDetailPageReducer/utils/getPricePortions"
import getMsrpPricePortions from "@secureo/common/reducers/productDetailPageReducer/utils/getMsrpPricePortions"
import validateConfigurations, {
	notifyUserOfChangedConfigurations,
} from "@secureo/common/reducers/productDetailPageReducer/utils/configurationGroups/validateConfigurations"

import {
	LockingSystemReducerState,
	DispatchLockingSystemReducer,
} from "reducers/lockingSystemReducer"
import { CylinderConfigurationVariant, ProductAttributes } from "@secureo/common/typings/Product"
import { CountryCode } from "@secureo/common/typings/CountryCode"
import getLockingSystemPricePortions, {
	getLockingSystemMsrpPricePortions,
} from "reducers/lockingSystemReducer/utils/getLockingSystemPricePortions"
import { ProviderContext } from "notistack"
import { LockingSystemComponent } from "@secureo/common/typings/LockingSystem"

type SetComponentCylinderConfigurationVariantPayload = Pick<
	LockingSystemReducerState,
	| "componentsByUuid"

	// Price
	| "singleNetPriceCents"
	| "singleGrossPriceCents"
	| "totalNetPriceCents"
	| "totalGrossPriceCents"

	// Msrp
	| "singleNetMsrpPriceCents"
	| "singleGrossMsrpPriceCents"
	| "totalNetMsrpPriceCents"
	| "totalGrossMsrpPriceCents"
>

export const getLockingSystemComponentWithUpdatedCylinderConfigurationVariant = (
	componentWithUpdatedSelectedConfigurations: LockingSystemComponent,
	cylinderConfigurationVariant: CylinderConfigurationVariant,
	taxRate: number,
	countryCode: CountryCode,
	enqueueSnackbar?: ProviderContext["enqueueSnackbar"],
	componentName?: string,
) => {
	const {
		variantsByVariantId,
		variantId,

		// attributes: prevAttributes,
		configurationsByConfigurationGroup: prevConfigurationsByConfigurationGroup,

		quantity,
	} = componentWithUpdatedSelectedConfigurations

	// const { variantsHaveUniqueProductPage = false } = prevAttributes

	const {
		id: productId,
		sku,
		// shortDescription,
		// name,
		// description,
		// masterVariant: { assets = [], images = [] },
	} = cylinderConfigurationVariant

	const attributes = getMergedAttributes(variantsByVariantId, variantId)
	const mergedAttributes: ProductAttributes = {
		...attributes,
		...cylinderConfigurationVariant.masterVariant.attributes,
	}

	const { configurationGroups: newConfigurationGroups = [] } = mergedAttributes

	// Validate configurations
	const validPrevConfigurations = validateConfigurations(
		prevConfigurationsByConfigurationGroup,
		newConfigurationGroups,
	)

	if (enqueueSnackbar) {
		notifyUserOfChangedConfigurations(
			validPrevConfigurations,
			prevConfigurationsByConfigurationGroup,
			enqueueSnackbar,
			componentName,
		)
	}

	const {
		singleNetPriceCents,
		singleGrossPriceCents,
		totalNetPriceCents,
		totalGrossPriceCents,
		pricePortions,
	} = getPricePortions(
		variantsByVariantId,
		variantId,
		taxRate,
		quantity,
		countryCode,
		validPrevConfigurations,
		cylinderConfigurationVariant,
	)

	const {
		singleNetMsrpPriceCents,
		singleGrossMsrpPriceCents,
		totalNetMsrpPriceCents,
		totalGrossMsrpPriceCents,
		msrpPricePortions,
	} = getMsrpPricePortions(quantity, taxRate, countryCode, attributes, {})

	const updatedComponent: LockingSystemComponent = {
		...componentWithUpdatedSelectedConfigurations,
		productId,
		sku,
		cylinderConfigurationVariant,
		attributes: mergedAttributes,

		// Price
		singleNetPriceCents,
		singleGrossPriceCents,
		totalNetPriceCents,
		totalGrossPriceCents,
		pricePortions,

		// Msrp
		singleNetMsrpPriceCents,
		singleGrossMsrpPriceCents,
		totalNetMsrpPriceCents,
		totalGrossMsrpPriceCents,
		msrpPricePortions,

		// TODO: Set new required configurations
		configurationsByConfigurationGroup: validPrevConfigurations,
	}

	return updatedComponent
}

export const setComponentCylinderConfigurationVariant = (
	dispatch: DispatchLockingSystemReducer,
	reducerState: LockingSystemReducerState,
	componentWithUpdatedSelectedConfigurations: LockingSystemComponent,
	cylinderConfigurationVariant: CylinderConfigurationVariant,
	enqueueSnackbar: ProviderContext["enqueueSnackbar"],
) => {
	const {
		taxRate,
		quantity,
		componentsByUuid,
		countryCode,
		lockingComponentsUuids,
		keyComponentsUuids,
	} = reducerState

	const { uuid, type } = componentWithUpdatedSelectedConfigurations
	const componentIndex = (
		type === "lock" ? lockingComponentsUuids : keyComponentsUuids
	).findIndex((componentUuid) => componentUuid === uuid)

	const componentName = `${type === "lock" ? "Tür" : "Schlüsselgruppe"} #${componentIndex + 1}`

	const updatedComponent = getLockingSystemComponentWithUpdatedCylinderConfigurationVariant(
		componentWithUpdatedSelectedConfigurations,
		cylinderConfigurationVariant,
		taxRate,
		countryCode,
		enqueueSnackbar,
		componentName,
	)

	componentsByUuid[updatedComponent.uuid] = updatedComponent

	const { singleNetPriceCents, singleGrossPriceCents, totalNetPriceCents, totalGrossPriceCents } =
		getLockingSystemPricePortions(componentsByUuid, quantity)

	const {
		singleNetMsrpPriceCents,
		singleGrossMsrpPriceCents,
		totalNetMsrpPriceCents,
		totalGrossMsrpPriceCents,
	} = getLockingSystemMsrpPricePortions(componentsByUuid, quantity)

	const payload: SetComponentCylinderConfigurationVariantPayload = {
		componentsByUuid,

		// Price
		singleNetPriceCents,
		singleGrossPriceCents,
		totalNetPriceCents,
		totalGrossPriceCents,

		// Msrp
		singleNetMsrpPriceCents,
		singleGrossMsrpPriceCents,
		totalNetMsrpPriceCents,
		totalGrossMsrpPriceCents,
	}

	dispatch({
		type: "SET_COMPONENT_CYLINDER_CONFIGURATION_VARIANT",
		payload,
	})
}
